import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Trash2, ChevronDown } from "lucide-react";
import SuccessToast from "../components/SuccessToast";
import AdminTopBar from "./AdminTopBar";
import AdminPagination from "./AdminPagination";
import { BusyTable } from "../components/TableBusyOverlay";
import usePagedListUi from "../hooks/usePagedListUi";
import { ADMIN_PAGE_SIZE, applyPagedSelect, totalPagesFromCount, uniqueIds } from "../lib/adminPagination";
import {
  adminCard,
  adminDangerBtn,
  adminEmpty,
  adminPageBg,
  adminPageTitle,
  adminPrimaryBtn,
  adminRow,
  adminSearchInput,
  adminStatusPill,
  adminTabActive,
  adminTabBase,
  adminTabIdle,
  adminTableHead,
  adminTableWrap,
  adminTd,
  adminTdName,
} from "./adminUi";
import { createAdminNotification } from "./notificationsService";
import SeatManagementModal from "./SeatManagementModal";
import { nextDuplicateTitle } from "../lib/duplicateEventTitle";
import { archiveEventsPastGracePeriod, getComputedEventStatus } from "../lib/eventPublicStatus";
import {
  archiveNeedsCancellationReason,
  formatEventWhen,
  isCancellationEmailEvent,
  recipientsForCancelledEvent,
} from "../lib/eventCancellationMail";
import { notifyBuyerEventCancelled } from "../lib/supportEmailNotify";
import { isMissingPolicyColumn, isMissingVenueColumn } from "../lib/venuesApi";

const PAGE_SIZE = ADMIN_PAGE_SIZE;
const MENU_WIDTH = 220;
const MENU_ESTIMATED_HEIGHT = 212;

const getComputedStatus = (event) => getComputedEventStatus(event);

const eventStatusLabel = (status) => {
  const normalized = String(status || "draft").toLowerCase();
  if (normalized === "published") return "Published";
  if (normalized === "past") return "Past";
  if (normalized === "archived") return "Archived";
  return "Draft";
};

const isMissingColumn = (error, columnName) => {
  if (!error) return false;
  const code = String(error.code || "");
  const message = String(error.message || "").toLowerCase();
  return code === "42703" || message.includes(String(columnName || "").toLowerCase());
};

const notifyFutureEventCancellations = async (archivedEvents, cancellationReason = "") => {
  const futureEvents = (archivedEvents || []).filter((event) => isCancellationEmailEvent(event));
  if (!futureEvents.length) return;

  try {
    const { data: tiers, error: tierError } = await supabase
      .from("ticket_tiers")
      .select("id, event_id")
      .in("event_id", futureEvents.map((event) => event.id));
    if (tierError) throw tierError;

    const tierIds = (tiers || []).map((tier) => tier.id);
    if (!tierIds.length) return;
    const eventIdByTier = Object.fromEntries((tiers || []).map((tier) => [tier.id, tier.event_id]));

    let ticketResult = await supabase
      .from("tickets")
      .select("id, buyer_id, ticket_tier_id, refunded_at")
      .in("ticket_tier_id", tierIds);
    if (ticketResult.error && isMissingColumn(ticketResult.error, "refunded_at")) {
      ticketResult = await supabase
        .from("tickets")
        .select("id, buyer_id, ticket_tier_id")
        .in("ticket_tier_id", tierIds);
    }
    if (ticketResult.error) throw ticketResult.error;

    const tickets = ticketResult.data || [];
    const buyerIds = [...new Set(tickets.map((ticket) => ticket.buyer_id).filter(Boolean))];
    if (!buyerIds.length) return;

    const { data: buyers, error: buyerError } = await supabase
      .from("users")
      .select("id, email")
      .in("id", buyerIds);
    if (buyerError) throw buyerError;

    const emailByBuyerId = Object.fromEntries((buyers || []).map((buyer) => [buyer.id, buyer.email]));

    for (const event of futureEvents) {
      const eventTickets = tickets.filter((ticket) => eventIdByTier[ticket.ticket_tier_id] === event.id);
      const recipients = recipientsForCancelledEvent(eventTickets, emailByBuyerId);
      for (const recipient of recipients) {
        await notifyBuyerEventCancelled({
          toEmail: recipient.email,
          eventTitle: event.title,
          eventDate: formatEventWhen(event),
          eventVenue: event.venue,
          ticketCount: recipient.ticketCount,
          cancellationReason,
        });
      }
    }
  } catch (mailError) {
    console.error("Cancellation emails failed:", mailError);
  }
};

const positionActionsMenu = (anchorRect) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  let left = anchorRect.right - MENU_WIDTH;
  if (left < 8) left = 8;
  if (left + MENU_WIDTH > viewportWidth - 8) {
    left = Math.max(8, viewportWidth - MENU_WIDTH - 8);
  }

  let top = anchorRect.bottom + 4;
  if (top + MENU_ESTIMATED_HEIGHT > viewportHeight - 8) {
    top = Math.max(8, anchorRect.top - MENU_ESTIMATED_HEIGHT - 4);
  }

  return { top, left };
};

const EventActionsMenu = ({ eventId, open, onOpen, onClose, onEdit, onManageSeats, onDuplicate, onRestore }) => {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const openMenu = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords(positionActionsMenu(rect));
    onOpen();
  };

  useEffect(() => {
    if (!open) return undefined;

    const handlePointer = (event) => {
      if (buttonRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      onClose();
    };

    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };

    const handleReposition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setCoords(positionActionsMenu(rect));
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [open, onClose]);

  const runAndClose = (action) => {
    onClose();
    action();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={`event-actions-${eventId}`}
        onClick={() => {
          if (open) {
            onClose();
            return;
          }
          openMenu();
        }}
        className={`${adminPrimaryBtn} gap-1.5`}
      >
        Actions <ChevronDown size={12} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          id={`event-actions-${eventId}`}
          role="menu"
          style={{ top: coords.top, left: coords.left, width: MENU_WIDTH }}
          className="fixed z-[80] bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
        >
          <button
            type="button"
            role="menuitem"
            className="w-full text-left px-4 min-h-11 py-2.5 text-xs font-bold text-gray-700 hover:bg-gray-50"
            onClick={() => runAndClose(onEdit)}
          >
            Edit Event Info
          </button>
          <button
            type="button"
            role="menuitem"
            className="w-full text-left px-4 min-h-11 py-2.5 text-xs font-bold text-gray-700 hover:bg-gray-50"
            onClick={() => runAndClose(onManageSeats)}
          >
            Manage Seats
          </button>
          <button
            type="button"
            role="menuitem"
            className="w-full text-left px-4 min-h-11 py-2.5 text-xs font-bold text-gray-700 hover:bg-gray-50"
            onClick={() => runAndClose(onDuplicate)}
          >
            Duplicate Event
          </button>
          {onRestore ? (
            <button
              type="button"
              role="menuitem"
              className="w-full text-left px-4 min-h-11 py-2.5 text-xs font-bold text-gray-700 hover:bg-gray-50 border-t border-gray-200"
              onClick={() => runAndClose(onRestore)}
            >
              Restore Event
            </button>
          ) : null}
        </div>,
        document.body
      )}
    </>
  );
};

const LiveEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All Events");
  const [searchTerm, setSearchTerm] = useState("");
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectedSeatMapEvent, setSelectedSeatMapEvent] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreEvent, setRestoreEvent] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [openActionsEventId, setOpenActionsEventId] = useState(null);
  const [duplicateEvent, setDuplicateEvent] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const listTopRef = useRef(null);
  const navigate = useNavigate();

  const showSuccessToast = (message) => {
    setToastMessage(message);
    window.setTimeout(() => {
      setToastMessage("");
    }, 2500);
  };

  const showErrorToast = (message) => {
    setErrorToastMessage(message);
    window.setTimeout(() => {
      setErrorToastMessage("");
    }, 3000);
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    let eventsQuery = supabase
      .from("events")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false });

    const needle = searchTerm.trim();
    if (needle) eventsQuery = eventsQuery.ilike("title", `%${needle}%`);
    if (activeTab !== "All Events") {
      eventsQuery = eventsQuery.eq("status", activeTab.toLowerCase());
    }

    const { data: eventsData, error: eventsError, count } = await applyPagedSelect(eventsQuery, currentPage, PAGE_SIZE);

    if (eventsError) {
      console.error("Error fetching live events data:", eventsError);
      setLoading(false);
      return;
    }

    const eventIds = uniqueIds(eventsData, "id");
    const { data: tiersData, error: tiersError } = eventIds.length
      ? await supabase.from("ticket_tiers").select("id, event_id, quantity_total").in("event_id", eventIds)
      : { data: [], error: null };
    const tierIds = uniqueIds(tiersData, "id");
    const { data: ticketsData, error: ticketsError } = tierIds.length
      ? await supabase.from("tickets").select("ticket_tier_id").in("ticket_tier_id", tierIds)
      : { data: [], error: null };

    if (tiersError || ticketsError) {
      console.error("Error fetching live events data:", tiersError || ticketsError);
      setLoading(false);
      return;
    }

    const archivedIds = new Set(await archiveEventsPastGracePeriod(supabase, eventsData || []));

    const tierTotalsByEvent = {};
    const tierToEventMap = {};

    (tiersData || []).forEach((tier) => {
      tierToEventMap[tier.id] = tier.event_id;
      tierTotalsByEvent[tier.event_id] =
        (tierTotalsByEvent[tier.event_id] || 0) + Number(tier.quantity_total || 0);
    });

    const soldByEvent = {};
    (ticketsData || []).forEach((ticket) => {
      const eventId = tierToEventMap[ticket.ticket_tier_id];
      if (!eventId) return;
      soldByEvent[eventId] = (soldByEvent[eventId] || 0) + 1;
    });

    const mergedEvents = (eventsData || []).map((event) => {
      const nextEvent = archivedIds.has(event.id) ? { ...event, status: "archived" } : event;
      const computedStatus = getComputedStatus(nextEvent);

      return {
        ...nextEvent,
        computedStatus,
        soldCount: soldByEvent[event.id] || 0,
        totalCapacity: tierTotalsByEvent[event.id] || 0,
      };
    });

    setEvents(mergedEvents);
    setTotalCount(Number(count) || mergedEvents.length);
    setLoading(false);
  }, [activeTab, currentPage, searchTerm]);

  useEffect(() => {
    fetchEvents();

    const eventsSubscription = supabase
      .channel("events-live")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "events" },
        () => {
          fetchEvents();
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "ticket_tiers" },
        () => {
          fetchEvents();
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "tickets" },
        () => {
          fetchEvents();
        }
      )
      .subscribe();

    return () => {
      eventsSubscription.unsubscribe();
    };
  }, [fetchEvents]);

  const filteredEvents = events;
  const totalPages = totalPagesFromCount(totalCount, PAGE_SIZE);
  const paginatedEvents = filteredEvents;
  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchTerm, activeTab, currentPage]);

  const selectableEvents = paginatedEvents.filter((event) => event.computedStatus !== "archived");
  const allSelectableChecked =
    selectableEvents.length > 0 && selectableEvents.every((event) => selectedIds.has(event.id));

  const toggleSelectAll = () => {
    if (allSelectableChecked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(selectableEvents.map((event) => event.id)));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCount = selectedIds.size;
  const selectedEvents = events.filter((event) => selectedIds.has(event.id));
  const needsCancellationReason = archiveNeedsCancellationReason(selectedEvents);
  const trimmedCancellationReason = cancellationReason.trim();

  const closeArchiveModal = () => {
    if (isArchiving) return;
    setShowArchiveModal(false);
    setCancellationReason("");
  };

  const confirmBatchArchive = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length || isArchiving) return;
    if (needsCancellationReason && !trimmedCancellationReason) return;

    setIsArchiving(true);

    const { data, error } = await supabase
      .from("events")
      .update({ status: "archived" })
      .in("id", ids)
      .select();

    if (error) {
      console.error("Error archiving events:", error);
      showErrorToast("Failed to delete events");
      setIsArchiving(false);
      return;
    }

    const updatedById = new Map((data || []).map((row) => [row.id, row]));
    setEvents((prev) => prev.map((event) => {
      const updated = updatedById.get(event.id);
      if (!updated) return event;
      const merged = { ...event, ...updated };
      return { ...merged, computedStatus: getComputedStatus(merged) };
    }));

    const archivedCount = updatedById.size || ids.length;
    const archivedIdSet = new Set(ids.map(String));
    const archivedEvents = events.filter((event) => archivedIdSet.has(String(event.id)));
    setShowArchiveModal(false);
    setCancellationReason("");
    setSelectedIds(new Set());
    setIsArchiving(false);
    showSuccessToast(`${archivedCount} event${archivedCount === 1 ? "" : "s"} moved to Archived`);

    await createAdminNotification({
      title: "Events archived",
      message: `${archivedCount} event${archivedCount === 1 ? " was" : "s were"} moved to archived.`,
      meta: "Events",
    });
    await notifyFutureEventCancellations(archivedEvents, trimmedCancellationReason);
  };

  const handleRestoreClick = (eventRow) => {
    setRestoreEvent(eventRow);
    setShowRestoreModal(true);
  };

  const confirmRestore = async (targetStatus) => {
    if (!restoreEvent?.id || isRestoring) return;

    setIsRestoring(true);

    const { data, error } = await supabase
      .from("events")
      .update({ status: targetStatus })
      .eq("id", restoreEvent.id)
      .select()
      .single();

    if (error) {
      console.error("Error restoring event:", error);
      showErrorToast("Failed to restore event");
      setIsRestoring(false);
      return;
    }

    const computedStatus = getComputedStatus(data);

    setEvents((prev) => prev.map((event) => (
      event.id === restoreEvent.id
        ? { ...event, ...data, computedStatus }
        : event
    )));

    setShowRestoreModal(false);
    setRestoreEvent(null);
    setIsRestoring(false);
    showSuccessToast(`Event restored as ${targetStatus}`);

    await createAdminNotification({
      title: "Event restored",
      message: `${data?.title || restoreEvent.title || "Event"} restored as ${targetStatus}.`,
      meta: "Events",
    });
  };

  const insertDuplicatedEvent = async (payload) => {
    let { data, error } = await supabase.from("events").insert(payload).select().single();

    if (isMissingColumn(error, "seat_plan_image_url")) {
      const withoutSeatPlan = { ...payload };
      delete withoutSeatPlan.seat_plan_image_url;
      const fallback = await supabase.from("events").insert(withoutSeatPlan).select().single();
      data = fallback.data;
      error = fallback.error;
    } else if (isMissingPolicyColumn(error)) {
      const withoutPolicy = { ...payload };
      delete withoutPolicy.venue_policy_snapshot;
      const fallback = await supabase.from("events").insert(withoutPolicy).select().single();
      data = fallback.data;
      error = fallback.error;
    } else if (isMissingVenueColumn(error)) {
      const withoutVenueLayout = { ...payload };
      delete withoutVenueLayout.venue_id;
      delete withoutVenueLayout.seat_layout_snapshot;
      delete withoutVenueLayout.venue_policy_snapshot;
      const fallback = await supabase.from("events").insert(withoutVenueLayout).select().single();
      data = fallback.data;
      error = fallback.error;
    }

    return { data, error };
  };

  const insertDuplicatedTiers = async (tiers) => {
    let { error } = await supabase.from("ticket_tiers").insert(tiers);

    if (isMissingColumn(error, "quantity_sold")) {
      const withoutSold = tiers.map(({ quantity_sold, ...rest }) => rest);
      const fallback = await supabase.from("ticket_tiers").insert(withoutSold);
      error = fallback.error;
    }

    if (isMissingColumn(error, "seat_areas")) {
      const withoutAreas = tiers.map(({ seat_areas, quantity_sold, ...rest }) => rest);
      const fallback = await supabase.from("ticket_tiers").insert(withoutAreas);
      error = fallback.error;
    }

    return { error };
  };

  const confirmDuplicate = async () => {
    if (!duplicateEvent?.id || isDuplicating) return;

    setIsDuplicating(true);

    try {
      const source = duplicateEvent;
      let { data: tiersData, error: tiersError } = await supabase
        .from("ticket_tiers")
        .select("name, price, quantity_total, section, seat_areas")
        .eq("event_id", source.id);

      if (tiersError && isMissingColumn(tiersError, "seat_areas")) {
        const fallbackTiers = await supabase
          .from("ticket_tiers")
          .select("name, price, quantity_total, section")
          .eq("event_id", source.id);
        tiersData = fallbackTiers.data;
        tiersError = fallbackTiers.error;
      }

      if (tiersError) throw tiersError;

      const payload = {
        title: nextDuplicateTitle(source.title, events.map((event) => event.title)),
        subtitle: source.subtitle || null,
        description: source.description || null,
        date: source.date || null,
        time: source.time || null,
        venue: source.venue || null,
        address: source.address || null,
        image_url: source.image_url || null,
        seat_plan_image_url: source.seat_plan_image_url || null,
        venue_id: source.venue_id || null,
        seat_layout_snapshot: source.seat_layout_snapshot || null,
        venue_policy_snapshot: source.venue_policy_snapshot || null,
        status: "draft",
      };

      const inserted = await insertDuplicatedEvent(payload);
      if (inserted.error) throw inserted.error;

      const newEventId = inserted.data.id;
      const newTiers = (tiersData || []).map((tier) => ({
        event_id: newEventId,
        name: tier.name,
        price: tier.price,
        quantity_total: tier.quantity_total,
        section: tier.section || null,
        seat_areas: tier.seat_areas || null,
        quantity_sold: 0,
      }));

      if (newTiers.length > 0) {
        const tierResult = await insertDuplicatedTiers(newTiers);
        if (tierResult.error) {
          const cleanup = await supabase.from("events").delete().eq("id", newEventId);
          if (cleanup.error) {
            console.error("Failed to clean up duplicated event after tier error:", cleanup.error);
          }
          throw tierResult.error;
        }
      }

      setDuplicateEvent(null);
      showSuccessToast("Event duplicated as draft");
      await createAdminNotification({
        title: "Event duplicated",
        message: `${inserted.data?.title || payload.title} was created as a draft copy.`,
        meta: "Events",
      });
      await fetchEvents();
    } catch (error) {
      console.error("Error duplicating event:", error);
      showErrorToast("Unable to duplicate this event. Please try again.");
    } finally {
      setIsDuplicating(false);
    }
  };

  const renderActions = (event) => (
    <EventActionsMenu
      eventId={event.id}
      open={openActionsEventId === event.id}
      onOpen={() => setOpenActionsEventId(event.id)}
      onClose={() => setOpenActionsEventId(null)}
      onEdit={() => navigate(`/admin/create-event?eventId=${event.id}`)}
      onManageSeats={() => setSelectedSeatMapEvent(event)}
      onDuplicate={() => setDuplicateEvent(event)}
      onRestore={event.computedStatus === "archived" ? () => handleRestoreClick(event) : undefined}
    />
  );

  return (
    <div className={adminPageBg}>
      <AdminTopBar />

      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-7xl mx-auto min-w-0">
        <SuccessToast message={toastMessage} />
        <SuccessToast message={errorToastMessage} variant="error" />
        
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-5">
          <h1 className={adminPageTitle}>Live Events</h1>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-64 min-w-0 flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input 
                type="text" 
                placeholder="Search event name..." 
                className={adminSearchInput}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button 
              onClick={() => navigate("/admin/create-event")}
              className={`${adminPrimaryBtn} w-full sm:w-auto shrink-0`}
            >
              <Plus size={16} /> Add New Event
            </button>
          </div>
        </div>

        <div className={adminCard} ref={listTopRef}>
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-gray-200 mb-4">
          <div className="flex flex-wrap gap-4 sm:gap-6">
            {["All Events", "Published", "Draft", "Past", "Archived"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`${adminTabBase} whitespace-nowrap ${
                  activeTab === tab ? adminTabActive : adminTabIdle
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          {activeTab !== "Archived" ? (
            <button
              type="button"
              onClick={() => {
                setCancellationReason("");
                setShowArchiveModal(true);
              }}
              disabled={selectedCount === 0}
              className={`${adminDangerBtn} h-8 px-3 gap-1.5 mb-2 disabled:opacity-40 disabled:pointer-events-none`}
            >
              <Trash2 size={13} /> Delete{selectedCount ? ` (${selectedCount})` : ""}
            </button>
          ) : null}
        </div>

        <BusyTable show={showSkeleton} tone="admin">
        <div className={adminTableWrap}>
          <table className="w-full min-w-[980px] text-left border-collapse">
            <thead>
              <tr className={adminTableHead}>
                <th className="w-10 px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    aria-label="Select all events on this page"
                    checked={allSelectableChecked}
                    disabled={selectableEvents.length === 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="px-4 py-3">Event Name</th>
                <th className="px-4 py-3">Date and Time</th>
                <th className="px-4 py-3 text-center">Venue</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Tickets Sold</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEvents.length > 0 ? (
                paginatedEvents.map((event) => (
                  <tr key={event.id} className={adminRow}>
                    <td className="px-4 py-4 text-center">
                      <input
                        type="checkbox"
                        aria-label={`Select ${event.title || "event"}`}
                        checked={selectedIds.has(event.id)}
                        disabled={event.computedStatus === "archived"}
                        onChange={() => toggleOne(event.id)}
                      />
                    </td>
                    <td className={adminTdName}>
                      {event.title}
                    </td>
                    <td className={adminTd}>
                      <div>{event.date}</div>
                      <div className="text-xs font-normal text-gray-500">{event.time}</div>
                    </td>
                    <td className={`${adminTd} text-center`}>
                      {event.venue}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`${adminStatusPill} ${
                        event.computedStatus === "published"
                          ? "bg-green-100 text-green-700"
                          : event.computedStatus === "past"
                            ? "bg-orange-100 text-orange-700"
                            : event.computedStatus === "archived"
                              ? "bg-slate-100 text-slate-700"
                            : "bg-gray-100 text-gray-700"
                      }`}>
                        {eventStatusLabel(event.computedStatus)}
                      </span>
                    </td>
                    <td className={`${adminTd} text-center`}>
                      {event.soldCount || 0}/{event.totalCapacity || "-"}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex justify-center items-center gap-2">
                        {renderActions(event)}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="7" className={adminEmpty}>{loading ? "Loading..." : "No events found."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </BusyTable>

        <AdminPagination
          className="mt-6"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
        />
        </div>

        {selectedSeatMapEvent && (
          <SeatManagementModal
            event={selectedSeatMapEvent}
            onClose={() => setSelectedSeatMapEvent(null)}
          />
        )}

        {showArchiveModal && selectedCount > 0 && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
              <h3 className="text-base font-black text-gray-900 uppercase tracking-tight mb-2">
                Delete {selectedCount} Event{selectedCount === 1 ? "" : "s"}?
              </h3>

              <p className={`text-sm text-gray-600 ${needsCancellationReason ? "mb-4" : "mb-6"}`}>
                {selectedCount === 1 ? "It" : "They"} will be removed from published and user-facing lists and moved to Archived. You can restore {selectedCount === 1 ? "it" : "them"} later.
              </p>

              {needsCancellationReason && (
                <label className="block mb-6">
                  <span className="block text-sm font-bold text-gray-900 mb-2">
                    Reason for cancellation
                  </span>
                  <textarea
                    value={cancellationReason}
                    onChange={(event) => setCancellationReason(event.target.value)}
                    rows={4}
                    disabled={isArchiving}
                    placeholder="Buyers will see this in the cancellation email."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-brand-maroon disabled:opacity-60"
                  />
                  <span className="block text-xs text-gray-500 mt-2">
                    Required because a selected event has not happened yet. Ticket holders will receive this reason by email.
                  </span>
                </label>
              )}

              <div className="flex flex-wrap gap-3 justify-end">
                <button
                  onClick={closeArchiveModal}
                  disabled={isArchiving}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmBatchArchive}
                  disabled={isArchiving || (needsCancellationReason && !trimmedCancellationReason)}
                  className="px-4 py-2 rounded-lg bg-brand-maroon text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60"
                >
                  {isArchiving ? "Deleting..." : "Yes, Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {duplicateEvent && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
            <div className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
              <h3 className="text-base font-black text-gray-900 uppercase tracking-tight mb-2">
                Duplicate Event?
              </h3>

              <p className="text-sm text-gray-600 mb-6">
                This will create a new draft event using this event&apos;s configuration. Ticket sales, orders, seat holds, and other event history will not be copied.
              </p>

              <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setDuplicateEvent(null)}
                  disabled={isDuplicating}
                  className="px-4 py-2 min-h-11 rounded-lg border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDuplicate}
                  disabled={isDuplicating}
                  className="px-4 py-2 min-h-11 rounded-lg bg-brand-maroon text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60"
                >
                  {isDuplicating ? "Duplicating..." : "Duplicate Event"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showRestoreModal && restoreEvent && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
              <h3 className="text-base font-black text-gray-900 uppercase tracking-tight mb-2">
                Restore Archived Event?
              </h3>

              <p className="text-sm text-gray-600 mb-6">
                Choose where to restore <span className="font-bold text-gray-800">{restoreEvent.title}</span>.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <button
                  onClick={() => confirmRestore("draft")}
                  disabled={isRestoring}
                  className="px-4 py-2 rounded-lg bg-gray-700 text-white text-sm font-bold hover:bg-gray-800 disabled:opacity-60"
                >
                  {isRestoring ? "Restoring..." : "Restore as Draft"}
                </button>
                <button
                  onClick={() => confirmRestore("published")}
                  disabled={isRestoring}
                  className="px-4 py-2 rounded-lg bg-brand-maroon text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60"
                >
                  {isRestoring ? "Restoring..." : "Restore as Published"}
                </button>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowRestoreModal(false);
                    setRestoreEvent(null);
                  }}
                  disabled={isRestoring}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50 disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default LiveEvents;
